import storage from '../storage';

const permissionCheck = (wild, perm, slug) => {
	if (wild) {
		// if a wild permission, we check if the slug has the portion before the asterisk
		// we pass if it does
		if (perm.slug.includes(slug.split('*')[0])) {
			return true;
		}
	}
	else {
		// otherwise we will check if there is a direct match of the permission
		if (perm.slug === slug) {
			return true;
		}
	}

	return false;
};

export default {
	namespaced: true,
	state: {
		status: '',
		isLoggedIn: storage.has('jwt'),
		user: {}
	},
	mutations: {
		request: state => {
			state.status = 'loading';
		},
		success: (state, data) => {
			state.status = 'success';
			state.isLoggedIn = true;
			state.user = data.user;
		},
		error: state => {
			state.status = 'error';
		},
		logout: state => {
			state.status = '';
			state.isLoggedIn = false;
			state.user = {};
		},
		UPDATE_USER: (state, user) => {
			state.user = user;
		}
	},
	actions: {
		/**
		 * Handles triggering login and storage of the JWT token.
		 * @param {object} context The store context reference.
		 * @param {object} data The data passed containing the user and token
		 * @returns {Promise}
		 */
		async login (context, data) {
			return new Promise(resolve => {
				context.commit('request');
				storage.put('jwt', data.token);

				context.commit('success', data);

				resolve();
			});
		},

		/**
		 * Handles triggering logout and deletion of the JWT token.
		 * @param {object} context The store context reference.
		 * @returns {Promise}
		 */
		async logout (context) {
			return new Promise(resolve => {
				storage.delete('jwt');
				this.isLoggedIn = false;

				context.commit('logout');
				resolve();
			});
		}
	},
	getters: {
		isLoggedIn: state => state.isLoggedIn,
		authStatus: state => state.status,
		currentUser: state => state.user,
		currentPermissions: state => {
			const roles = state.user.roles;
			const permissionList = [];

			for (const role of roles) {
				for (const permission of role.permissions) {
					if (!permissionList.includes(permission.slug)) {
						permissionList.push(permission.slug);
					}
				}
			}

			return permissionList;
		},
		hasPermission: (state) => (permissionSlug, strict = true, clientId = null) => {
			// get the roles the user has
			const roles = state.user.roles || [];
			// filter out the global roles
			const globalRoles = roles.filter(element => element.global);

			// if the permission has an asterisk in it, there may be more than one permission that allows an action
			// we will compare on the portion before the asterisk
			const wildPermission = permissionSlug.includes('*');

			// global roles take precedence, iterate them first
			for (const role of globalRoles) {
				// go through the list of permissions for the role
				for (const permission of role.permissions) {
					// if the user has super_admin permission, we auto pass
					if (permission.slug === 'super_admin') {
						return true;
					}

					if (permissionCheck(wildPermission, permission, permissionSlug)) {
						return true;
					}
				}
			}

			// if we have a client id, we need to check the client roles
			if (strict) {
				// we only process strict if we have a client id, otherwise we fail
				if (clientId) {
					// we need a strict comparison, so role list consists of roles belonging to the provided clientId
					const clientRoles = roles.filter(element => !element.global && element.client_ids.includes(clientId));

					for (const role of clientRoles) {
						// go through the list of permissions for the role
						for (const permission of role.permissions) {
							if (permissionCheck(wildPermission, permission, permissionSlug)) {
								return true;
							}
						}
					}
				}
			}
			else {
				// we are not being strict in the permission so any role with the permission counts
				const clientRoles = roles.filter(element => !element.global);

				for (const role of clientRoles) {
					// go through the list of permissions for the role
					for (const permission of role.permissions) {
						if (permissionCheck(wildPermission, permission, permissionSlug)) {
							return true;
						}
					}
				}
			}

			// if no conditions are met, user does not have permission
			return false;
		}
	}
};
